import {
  Box,
  Button,
  Container,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TablePagination,
  AppBar,
  Toolbar,
} from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import { makeStyles } from "@mui/styles";

//#Service Apis
import { gameScoreBaseUrl } from "../../services/AzureStorage";
import { AppDataContext } from "../../common/appContext";
import { TeamPlayerServices } from "../../services/teamPlayers.Service";

//#Icons
import JpgIcon from "../../sharedComponents/customIcons/JpgIcon";
import PngIcon from "../../sharedComponents/customIcons/PngIcon";
import TxtIcon from "../../sharedComponents/customIcons/TxtIcon";
import WordIcon from "../../sharedComponents/customIcons/WordIcon";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import Loader from "../../sharedComponents/loader/loader";

const useStyles = makeStyles((theme) => ({
  headerBox: {
    background: "#c1d0ed",
  },
  headerText: {
    marginBottom: theme.spacing(0.5),
    color: "rgb(29, 66, 138)",
  },
  tableRow: {
    "&:nth-child(odd)": {
      background: "#f5f5f5",
    },
    "& .MuiTableCell-root": {
      borderBottom: "none",
    },
  },
  iconStyle: {
    width: "26px",
    height: "26px",
    cursor: "pointer",
  },
  appbarBackground: {
    backgroundColor: "#fff",
    color: "#1d428a",
  },
  title: {
    flexGrow: 1,
  },
}));

const ViewTeamRoster = () => {
  const classes = useStyles();

  //#Get data from Context.
  const { user } = useContext(AppDataContext);
  let userAppRoles = user.appRoles || [];

  const [loading, setLoading] = useState(false);
  const [documentList, setDocumentList] = useState([]);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [page, setPage] = React.useState(0);

  useEffect(() => {
    getTeamRosters();
  }, []);

  const getTeamRosters = async () => {
    setLoading(true);
    setDocumentList([]);

    try {
      const { data } = await TeamPlayerServices.getTeamRostersByEnum();
      console.log("result", data);
      setDocumentList(data);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleFileClick = async (file, fileExtension) => {
    const url = gameScoreBaseUrl(file);
    if (["png", "pdf", "jpg"].includes(fileExtension)) {
      //window.open(url, "_blank");
      openFileInNewTab(url, file);
    } else {
      await fetch(url).then((response) =>
        response.blob().then((blob) => {
          let url = window.URL.createObjectURL(new Blob([blob]));
          let a = document.createElement("a");
          a.href = url;
          a.download = file;
          a.click();
          window.URL.revokeObjectURL(url);
        })
      );
    }
  };

  const getFileSourceIcon = (type) => {
    switch (type) {
      case "png":
        return <PngIcon className={classes.iconStyle} color="secondary" />;
      case "jpg":
        return <JpgIcon className={classes.iconStyle} color="secondary" />;
      case "txt":
        return <TxtIcon className={classes.iconStyle} />;
      case "docx":
        return (
          <WordIcon
            sx={{
              color: "#009688",
              width: "26px",
              height: "26px",
              cursor: "pointer",
            }}
          />
        );
      case "pdf":
        return (
          <PictureAsPdfIcon className={classes.iconStyle} color="secondary" />
        );
      case "xlsx":
        return (
          <img
            alt="excel icon"
            src="../../../../playerProfile/excel.png"
            height="23px"
            width="23px"
          />
        );
      default:
        return <TxtIcon className={classes.iconStyle} />;
    }
  };

  function getFileExtension(filename) {
    if (typeof filename !== "string") return "";
    const parts = filename.split(".");
    return parts.length > 1 ? parts.pop().toLowerCase() : "";
  }

  const openFileInNewTab = async (url) => {
    await fetch(url)
      .then((response) => response.blob())
      .then((blob) => {
        const fileUrl = window.URL.createObjectURL(blob);
        const newTab = window.open(fileUrl, "_blank");

        // Revoke the object URL when the new tab is closed
        newTab.addEventListener("unload", () => {
          window.URL.revokeObjectURL(fileUrl);
        });
      });
  };

  return (
    <>
      <Loader isLoading={loading} />
      <AppBar position="static" className={classes.appbarBackground}>
        <Toolbar>
          <Typography
            variant="h6"
            sx={{ fontSize: "clamp(1rem, 2vw, 1.5rem)" }}
          >
            Team Rosters
          </Typography>
        </Toolbar>
      </AppBar>
      <Container maxWidth={false}>
        <Paper elevation={3} sx={{ my: 2 }}>
          <Box
            display="flex"
            alignItems="center"
            flexDirection="row"
            className={classes.headerBox}
          >
            <Box display="flex" pl={2}>
              <Typography className={`${classes.headerText} headerTitle`}>
                View Team Rosters
              </Typography>
            </Box>
          </Box>

          <Box p={2}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableCell sx={{ padding: "0.2rem" }}>
                    Document Name
                  </TableCell>
                  <TableCell sx={{ padding: "0.2rem" }}>Action</TableCell>
                </TableHead>
                <TableBody>
                  {documentList.length > 0 ? (
                    documentList
                      .slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage
                      )
                      .map((document, index) => {
                        const { childID, childName, description } = document;

                        const fileExtension = getFileExtension(childName);
                        return (
                          <TableRow key={index} className={classes.tableRow}>
                            <TableCell style={{ padding: "0.2rem" }}>
                              {childName}
                            </TableCell>

                            <TableCell style={{ padding: "0.2rem" }}>
                              <Stack direction="row" spacing={2}>
                                <Box>
                                  <Button
                                    onClick={() =>
                                      handleFileClick(childName, fileExtension)
                                    }
                                  >
                                    {getFileSourceIcon(fileExtension)}
                                  </Button>
                                </Box>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        );
                      })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        No Records Found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[10]}
              component="div"
              count={
                documentList && documentList.length > 0
                  ? documentList.length
                  : 0
              }
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{ width: "100%" }}
            />
          </Box>
        </Paper>
      </Container>
    </>
  );
};

export default ViewTeamRoster;
